/**
 * HITL decision payload builders — pure, framework-free.
 *
 * The decision a human sends to resolve a HITL gate. Extracted from the Core
 * Shell task inbox (`TaskDecisionSlideOut`) so the exact same payloads are
 * built whether the decision comes from the inbox slide-out or a chat gate
 * card — the resolve endpoint (`POST /v1/tasks/hitl/resolve`) is identical for
 * both, and both engines read the same keys.
 *
 * No React, no network: these are the unit-testable core of the decision.
 */

/**
 * Build an approval-gate decision (`render.ui_block === 'hitl_approval'`).
 *
 * `gate` is omitted when falsy — a gate whose `resumeMatch` carries no `gate`
 * key must not receive an empty one. `rejection_type: 'minor'` is added only
 * on reject: the strategy gates read it, and gates that don't simply ignore
 * the extra key.
 *
 * @param {{ gate?: string, status: 'approved'|'rejected', comments?: string }} params
 * @returns {{ gate?: string, status: string, rejection_type?: string, comments: string }}
 */
export const buildApprovalDecision = ({ gate, status, comments } = {}) => ({
  ...(gate ? { gate } : {}),
  status,
  ...(status === 'rejected' ? { rejection_type: 'minor' } : {}),
  comments: comments ?? '',
});

/**
 * Shared envelope for the brief-gate contract (`buildQaDecision` /
 * `buildChoiceDecision`): both send `{gate, done, message, research_requested,
 * research_questions}` with the same fallbacks, so the shape lives in one
 * place. `answers` is included only when the caller provides it (choice).
 *
 * @param {string|undefined} gate
 * @param {{
 *   done: boolean,
 *   message?: string,
 *   answers?: Array<object>,
 *   researchRequested?: boolean,
 *   researchQuestions?: string,
 * }} params
 */
const withGateEnvelope = (gate, { done, message, answers, researchRequested, researchQuestions }) => ({
  ...(gate ? { gate } : {}),
  done,
  message: message || 'Proceed.',
  ...(answers ? { answers } : {}),
  research_requested: Boolean(researchRequested),
  research_questions: researchQuestions ?? '',
});

/**
 * Build a conversational-gate (Q&A) decision — `render.ui_block === 'hitl_qa'`.
 *
 * The contract is `{gate, done, message, research_requested, research_questions}`
 * (e.g. the discovery brief gate): `done: false` feeds `message` back into the
 * interview loop, so the engine requires it non-empty; `done: true` proceeds to
 * synthesis, and the message defaults to a neutral marker because gate renders
 * embed the whole payload. `research_requested`/`research_questions` let the
 * turn ask the workflow to search the team's Confluence/Jira before the agent
 * replies; both keys are ALWAYS sent (empties allowed) so the engine's
 * single-comparison conditions always have a key to read.
 *
 * @param {{
 *   gate?: string,
 *   done: boolean,
 *   message?: string,
 *   researchRequested?: boolean,
 *   researchText?: string,
 * }} params
 * @returns {{
 *   gate?: string,
 *   done: boolean,
 *   message: string,
 *   research_requested: boolean,
 *   research_questions: string,
 * }}
 */
export const buildQaDecision = ({
  gate,
  done,
  message,
  researchRequested,
  researchText,
} = {}) => {
  const text = (message ?? '').trim();
  const wantsResearch = Boolean(researchRequested);
  if (!done && !text && !wantsResearch) {
    throw new Error(
      'A Q&A answer needs a non-empty message or a workspace-research request ' +
        '(contract: done:false).',
    );
  }
  return withGateEnvelope(gate, {
    done,
    message: text,
    researchRequested: wantsResearch,
    researchQuestions: wantsResearch ? (researchText ?? text).trim() : '',
  });
};

/**
 * Build an option-chips (choice) decision — `render.ui_block === 'hitl_choice'`.
 *
 * The choice card renders the structured `gaps` questions (each `{id, question,
 * options, allow_custom}`); the human picks an option, types a custom answer,
 * and/or marks a question for workspace research. This produces the brief-gate
 * contract shared with {@link buildQaDecision}:
 *
 *   {gate, done, message, answers:[{id, question, selected, custom}],
 *    research_requested, research_questions}
 *
 * ALL keys are always present (empties allowed) — the workflow's `qa_reply`
 * reads `message` and its conditions read `research_requested`, so a missing
 * key would either raise a reference error or silently skip research.
 * `message` is the human-readable composition the agent actually receives.
 *
 * @param {{
 *   gate?: string,
 *   done?: boolean,
 *   questions?: Array<{ id: string, question: string, allow_custom?: boolean }>,
 *   selections?: Record<string, { selected?: string, custom?: string, research?: boolean }>,
 * }} params
 * @returns {{
 *   gate?: string,
 *   done: boolean,
 *   message: string,
 *   answers: Array<{ id: string, question: string, selected: string, custom: string }>,
 *   research_requested: boolean,
 *   research_questions: string,
 * }}
 */
export const buildChoiceDecision = ({
  gate,
  done = false,
  questions = [],
  selections = {},
} = {}) => {
  const answers = [];
  const answeredLines = [];
  const researchQuestions = [];

  for (const q of questions) {
    const pick = selections[q.id] || {};
    const selected = (pick.selected ?? '').trim();
    const custom = (pick.custom ?? '').trim();
    const wantsResearch = Boolean(pick.research);

    answers.push({
      id: q.id,
      question: q.question,
      selected,
      custom,
    });

    const answerText = custom || selected;
    if (answerText) {
      answeredLines.push(`Q: ${q.question}\nA: ${answerText}`);
    }
    if (wantsResearch) {
      researchQuestions.push(q.question);
    }
  }

  const wantsResearch = researchQuestions.length > 0;
  const message = answeredLines.join('\n\n');

  if (!done && !message && !wantsResearch) {
    throw new Error(
      'Answer at least one question, request workspace research, or finish ' +
        '(contract: done:false).',
    );
  }

  return withGateEnvelope(gate, {
    done,
    message,
    answers,
    researchRequested: wantsResearch,
    researchQuestions: researchQuestions.join('\n- '),
  });
};

/**
 * Build a document-pick decision — `render.ui_block === 'hitl_pick'`.
 *
 * The resume contract is `{gate, document_uuid, file_uuid, name, file_type}`
 * and the workflow reads EVERY key — a missing key fails the run with a
 * reference error, so all keys are always sent (empty strings allowed).
 * Drive picks carry `document_uuid`; local-attach picks carry `file_uuid`
 * (+ name/type) and an empty `document_uuid` for the workflow to resolve.
 *
 * @param {{
 *   gate?: string,
 *   documentUuid?: string,
 *   fileUuid?: string,
 *   name?: string,
 *   fileType?: string,
 * }} params
 * @returns {{
 *   gate?: string,
 *   document_uuid: string,
 *   file_uuid: string,
 *   name: string,
 *   file_type: string,
 * }}
 */
export const buildDocumentPickDecision = ({
  gate,
  documentUuid,
  fileUuid,
  name,
  fileType,
} = {}) => {
  const uuid = (documentUuid ?? '').trim();
  const file = (fileUuid ?? '').trim();
  if (!uuid && !file) {
    throw new Error(
      'A document-pick decision needs a document_uuid or a file_uuid.',
    );
  }
  return {
    ...(gate ? { gate } : {}),
    document_uuid: uuid,
    file_uuid: file,
    name: (name ?? '').trim(),
    file_type: (fileType ?? '').trim(),
  };
};

/**
 * Thrown by {@link parseCustomDecision} when the editor text is not valid JSON,
 * so a caller can distinguish it from a network failure and show the right
 * message.
 */
export class HitlDecisionJsonError extends Error {
  constructor(message = 'HITL decision is not valid JSON') {
    super(message);
    this.name = 'HitlDecisionJsonError';
  }
}

/**
 * Parse a custom (non-approval) gate decision from the JSON editor.
 *
 * Custom gates (e.g. brief-review, document pick) define their own resume
 * keys; the human edits raw JSON. Throws {@link HitlDecisionJsonError} on
 * malformed input rather than returning a partial object.
 *
 * @param {string} jsonText
 * @returns {object}
 */
export const parseCustomDecision = (jsonText) => {
  try {
    return JSON.parse(jsonText);
  } catch {
    throw new HitlDecisionJsonError();
  }
};
